
import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Chip,
    IconButton,
    Divider,
    Avatar,
    Tooltip,
    CircularProgress,
    InputAdornment,
    Popover,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import {
    CalendarToday as CalendarIcon,
    ArrowBackIos as ArrowLeftIcon,
    ArrowForwardIos as ArrowRightIcon,
    Person as PersonIcon,
    ArrowBackIosNew as BackIcon,
    Add as AddIcon,
    SwapHoriz as SwapIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, addMonths, subMonths } from 'date-fns';
import Brandcrumb from '../../../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../../../utils/request';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../../utils/useDebounce';

import usePrint from '../../../../../../utils/usePrint';
import PrintableContainer from '../../../../../../components/PrintableContainer';
import PrintButtons from '../../../../../../components/PrintButtons';
import { DailyAttendance, Employee, MonthlyAttendanceData, SalaryCalculation } from '../types';

// --- Interface Definitions ---

// Convert 24-hour to 12-hour format with AM/PM
const formatTo12Hour = (time24: string): string => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Calculate salary with proper attendance handling
const calculateSalary = (
    baseSalary: number,
    attendanceData: DailyAttendance[],
    hoursPerDay: number = 8
): SalaryCalculation => {
    const WORKING_DAYS = 30;
    const OVERTIME_MULTIPLIER = 1.5;

    const dailyRate = baseSalary / WORKING_DAYS;
    const hourlyRate = dailyRate / hoursPerDay;
    const overtimeRate = hourlyRate * OVERTIME_MULTIPLIER;

    let presentDays = 0;
    let halfDays = 0;
    let absentDays = 0;
    let paidLeaves = 0;
    let weekOffs = 0;
    let holidays = 0;
    let overtimeHours = 0;
    let totalAdvance = 0;

    attendanceData.forEach(day => {
        switch (day.attendance_status) {
            case 'Present':
                presentDays++;
                overtimeHours += day.overtime_hour || 0;
                break;

            case 'Half Day':
                halfDays++;
                break;

            case 'Paid Leave':
                paidLeaves++;
                break;

            case 'Week Off':
                weekOffs++;
                break;

            case 'Holiday':
                holidays++;
                break;

            case 'Absent':
                absentDays++;
                break;
        }

        totalAdvance += day.advance_amount || 0;
    });

    // ✅ Salary components
    const presentSalary = presentDays * dailyRate;
    const halfDaySalary = halfDays * dailyRate * 0.5;
    const paidLeaveSalary =
        (paidLeaves + weekOffs + holidays) * dailyRate;
    const overtimeSalary = overtimeHours * overtimeRate;

    const totalEarnings =
        presentSalary +
        halfDaySalary +
        paidLeaveSalary +
        overtimeSalary;

    const deductions = totalAdvance;

    const netSalary = Math.max(0, totalEarnings - deductions);

    return {
        baseSalary,
        workingDays: WORKING_DAYS,
        presentDays,
        halfDays,
        absentDays,
        paidLeaves,
        weekOffs,
        holidays,
        overtimeHours,
        totalAdvance,
        dailyRate,
        hourlyRate,
        overtimeRate,
        presentSalary,
        halfDaySalary,
        paidLeaveSalary,
        overtimeSalary,
        totalEarnings,
        deductions,
        netSalary
    };
};


const AttendanceListPerMonthOneEmployee: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
    const [attendanceData, setAttendanceData] = useState<DailyAttendance[]>([]);
    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState<Employee | null>(null);
    const [monthlySummary, setMonthlySummary] = useState<MonthlyAttendanceData['summary'] | null>(null);

    // Employee change dropdown states
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const debouncedSearch = useDebounce(searchQuery, 500);

    const { isPrinting, handlePrint } = usePrint();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const onPrint = () => {
        // force re-render before invoking print
        requestAnimationFrame(() => {
            setTimeout(() => {
                handlePrint('monthly-attendance', {
                    title: `Monthly Attendance - ${employeeData?.name || 'Employee'}`,
                    fileName: `Monthly Attendance - ${employeeData?.name || 'Employee'}`,
                    landscape: false,
                    showHeader: true,
                    showFooter: false,
                    customStyles: `
                    table { width: 100% !important; border-collapse: collapse !important; }
                    th, td { border: 1px solid #ddd !important; padding: 6px !important; }
                    th { background-color: #f5f5f5 !important; font-weight: bold !important; }
                    .no-print { display: none !important; }
                    .section-heading{ font-size:14px; font-weight:bold; padding:10px 0px; margin:0px}
                    .section-sub-heading{ font-size:10px; font-weight:bold; padding:10px 0px; margin:0px}
                    .d-flex {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 12px; justify-content:space-between ;
                           }
                    .summary-section{
                     padding:0px 0px 14px 0px; 
                    }
                    `
                });
            }, 150); // small delay ensures browser repaints
        });
    };

    // Fetch all employees for dropdown
    const fetchAllEmployees = async () => {
        try {
            setLoadingEmployees(true);
            const res = await request({
                method: 'get',
                url: 'employee/get-all-employees'
            });

            if (res?.status === "success" && res?.data) {
                const employeeList = res.data.map((emp: any) => ({
                    _id: emp._id,
                    name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
                    first_name: emp.first_name,
                    last_name: emp.last_name,
                    employee_type: emp.job?.employee_type || 'Full-time',
                    base_salary: emp.job?.base_salary || 0,
                    photo: emp.photo
                }));
                setEmployees(employeeList);
            } else {
                toast.error('Failed to fetch employees list');
            }
        } catch (error: any) {
            console.error('Error fetching employees:', error);
            toast.error(error?.message || 'Failed to fetch employees list');
        } finally {
            setLoadingEmployees(false);
        }
    };

    // Fetch monthly attendance data
    const fetchMonthlyAttendance = async (employeeId: string, month: string) => {
        try {
            setLoading(true);

            // Format month to YYYY-MM
            const formattedMonth = format(new Date(month), 'yyyy-MM');

            const res = await request({
                method: 'get',
                url: `attendance/employee-monthly-attendance/${employeeId}?month=${formattedMonth}`
            });

            if (res?.status === "success" && res?.data) {
                const data = res.data;

                // Create employee object from response
                const employee: Employee = {
                    _id: data.employee?._id || employeeId,
                    name: data.employee?.name || `${data.employee?.first_name || ''} ${data.employee?.last_name || ''}`.trim() || 'Employee',
                    first_name: data.employee?.first_name,
                    last_name: data.employee?.last_name,
                    employee_type: data.employee?.employee_type || data.employee?.job?.employee_type || 'Full-time',
                    base_salary: data.employee?.base_salary || data.employee?.job?.base_salary || 0,
                    photo: data.employee?.photo
                };

                setEmployeeData(employee);
                setAttendanceData(data.attendance || []);
                setMonthlySummary(data.summary);

                // Sort attendance data by date
                if (data.attendance && data.attendance.length > 0) {
                    const sortedAttendance = [...data.attendance].sort((a, b) =>
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    setAttendanceData(sortedAttendance);
                }
            } else {
                toast.error(res?.message || 'Failed to fetch attendance data');
                setAttendanceData([]);
                setEmployeeData(null);
                setMonthlySummary(null);
            }
        } catch (error: any) {
            console.error('Error fetching monthly attendance:', error);
            toast.error(error?.message || 'Failed to fetch attendance data');
            setAttendanceData([]);
            setEmployeeData(null);
            setMonthlySummary(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            const month = format(selectedMonth, 'yyyy-MM');
            fetchMonthlyAttendance(id, month);

            // Also fetch all employees for dropdown
            fetchAllEmployees();
        }
    }, [id, selectedMonth]);

    // Filter employees based on search
    const filteredEmployees = employees.filter(emp => {
        if (!debouncedSearch.trim()) return true;
        const searchLower = debouncedSearch.toLowerCase();
        return (
            emp.name.toLowerCase().includes(searchLower) ||
            emp.first_name?.toLowerCase().includes(searchLower) ||
            emp.last_name?.toLowerCase().includes(searchLower) ||
            emp.employee_type.toLowerCase().includes(searchLower)
        );
    });

    // Employee change handlers
    const handleChangeEmployeeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseEmployeeDropdown = () => {
        setAnchorEl(null);
        setSearchQuery("");
    };

    const handleSelectEmployee = (employeeId: string) => {
        // Navigate to the same page with new employee ID
        navigate(`/shopManagement/employeeManagement/attendance/${employeeId}`);
        handleCloseEmployeeDropdown();
    };

    // Navigation handlers
    const handlePreviousMonth = () => {
        setSelectedMonth(prev => subMonths(prev, 1));
    };

    const handleNextMonth = () => {
        setSelectedMonth(prev => addMonths(prev, 1));
    };

    const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedMonth(new Date(event.target.value + '-01'));
    };

    // Status color mapping
    const getStatusColor = (status: DailyAttendance['attendance_status']) => {
        switch (status) {
            case 'Present': return 'success';
            case 'Absent': return 'error';
            case 'Half Day': return 'warning';
            case 'Paid Leave': return 'info';
            case 'Week Off': return 'secondary.contrastText';
            case 'Holiday': return 'primary';
            default: return 'default';
        }
    };

    // Status short form
    const getStatusShort = (status: DailyAttendance['attendance_status']) => {
        switch (status) {
            case 'Present': return 'P';
            case 'Absent': return 'A';
            case 'Half Day': return 'HD';
            case 'Paid Leave': return 'PL';
            case 'Week Off': return 'WO';
            case 'Holiday': return 'H';
            default: return '';
        }
    };
    // Calculate salary details based on actual data
    const salaryDetails = employeeData?.base_salary
        ? calculateSalary(employeeData.base_salary, attendanceData)
        : {
            baseSalary: 0,
            workingDays: 0,
            presentDays: 0,
            halfDays: 0,
            absentDays: 0,
            paidLeaves: 0,
            weekOffs: 0,
            holidays: 0,
            overtimeHours: 0,
            totalAdvance: 0,
            dailyRate: 0,
            hourlyRate: 0,
            overtimeRate: 0,
            presentSalary: 0,
            halfDaySalary: 0,
            paidLeaveSalary: 0,
            overtimeSalary: 0,
            totalEarnings: 0,
            netSalary: 0,
            deductions: 0
        };

    // Use API summary if available, otherwise use calculated summary
    const summaryData = monthlySummary || {
        totalDays: attendanceData.length,
        presentDays: salaryDetails.presentDays,
        absentDays: salaryDetails.absentDays,
        halfDays: salaryDetails.halfDays,
        paidLeaveDays: salaryDetails.paidLeaves,
        weekOffDays: salaryDetails.weekOffs,
        holidayDays: salaryDetails.holidays,
        totalWorkingHours: attendanceData.reduce((sum, day) => sum + (day.working_hour || 0), 0),
        totalOvertimeHours: attendanceData.reduce((sum, day) => sum + (day.overtime_hour || 0), 0),
        totalAdvance: attendanceData.reduce((sum, day) => sum + (day.advance_amount || 0), 0),
        attendancePercentage: attendanceData.length > 0
            ? Math.round((salaryDetails.presentDays / attendanceData.length) * 100)
            : 0
    };

    const monthYear = format(selectedMonth, 'MMMM yyyy');
    const isEmployeeDropdownOpen = Boolean(anchorEl);

    // Summary cards data
    const summaryCards = [
        { label: 'Present', value: summaryData.presentDays, short: 'P', color: 'success' as const },
        { label: 'Absent', value: summaryData.absentDays, short: 'A', color: 'error' as const },
        { label: 'Half Day', value: summaryData.halfDays, short: 'HD', color: 'warning' as const },
        { label: 'Paid Leave', value: summaryData.paidLeaveDays, short: 'PL', color: 'info' as const },
        { label: 'Week Off', value: summaryData.weekOffDays, short: 'WO', color: 'secondary.contrastText' as const },
        { label: 'Holiday', value: summaryData.holidayDays, short: 'H', color: 'primary' as const },
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading attendance data...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}>
                <Brandcrumb />
                <Chip
                    onClick={() => navigate(-1)}
                    icon={<BackIcon sx={{ fontSize: "14px" }} />}
                    label="Back"
                    variant="filled"
                    color="secondary"
                    sx={{
                        fontSize: { xs: '0.7rem', sm: '14px' },
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        }
                    }}
                />
            </Box>

            <Card elevation={4}>
                <CardContent>
                    {/* Employee Information Section with Change Button - Fixed Responsive Design */}
                    <Paper elevation={0} sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 3,
                        bgcolor: 'primary.lighter',
                        borderRadius: 1
                    }}>
                        <Box display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            gap={{ xs: 2, sm: 0 }}
                            flexWrap={'wrap'}
                        >
                            {/* Employee Info - Left Side */}
                            <Box display="flex" alignItems="center" gap={2} width={{ xs: '100%', sm: 'auto' }}>
                                <Avatar
                                    src={employeeData?.photo}
                                    sx={{
                                        bgcolor: 'primary.main',
                                        width: { xs: 50, sm: 60 },
                                        height: { xs: 50, sm: 60 },
                                        border: '2px solid white',
                                        flexShrink: 0
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                                        <Typography
                                            variant={isMobile ? "h6" : "h5"}
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                                lineHeight: 1.2,
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {employeeData?.name || 'Employee Not Found'}
                                        </Typography>
                                        <Tooltip title="Change Employee" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleChangeEmployeeClick}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        bgcolor: 'primary.lighter'
                                                    },
                                                    flexShrink: 0
                                                }}
                                            >
                                                <SwapIcon fontSize={isMobile ? "small" : "medium"} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" rowGap={0.5}>
                                        {employeeData?.employee_type && (
                                            <Chip
                                                label={employeeData.employee_type}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: { xs: 20, sm: 24 },
                                                    fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                                    flexShrink: 0
                                                }}
                                            />
                                        )}
                                        {employeeData?.base_salary && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                Base Salary: ₹{employeeData.base_salary}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Action Buttons - Right Side */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, sm: 1.5 },
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                mt: { xs: 1, sm: 0 }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: 1, sm: 1.5 },
                                    flexWrap: 'wrap'
                                }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate('/shopManagement/employeeManagement/employees/addEmployee')}
                                        size="small"
                                        sx={{
                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                            height: 32,
                                            minWidth: { xs: 'auto', sm: 120 },
                                            px: { xs: 1, sm: 2 }
                                        }}
                                    >
                                        {isMobile ? 'Add' : 'Add New Employee'}
                                    </Button>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: { xs: 'auto', sm: 80 } }}>
                                        <PrintButtons
                                            onPrint={onPrint}
                                            isPrinting={isPrinting}
                                            printLabel={isMobile ? 'Print' : 'Print'}
                                            pdfLabel='Export PDF'
                                            disabled={loading || !employeeData}
                                            showPDF={false}
                                        />
                                    </Box>
                                </Box>

                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={() => window.location.reload()}
                                        size={isMobile ? "small" : "medium"}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <RotateLeftIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Employee Change Dropdown with Search */}
                        <Popover
                            open={isEmployeeDropdownOpen}
                            anchorEl={anchorEl}
                            onClose={handleCloseEmployeeDropdown}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            PaperProps={{
                                sx: {
                                    width: { xs: 'calc(100vw - 32px)', sm: 400 },
                                    maxHeight: 400,
                                    mt: 1,
                                    boxShadow: 3,
                                    maxWidth: '90vw'
                                }
                            }}
                        >
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search employee by name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: loadingEmployees && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={20} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>

                            <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
                                {loadingEmployees ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading employees...
                                        </Typography>
                                    </Box>
                                ) : filteredEmployees.length === 0 ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {debouncedSearch.trim() ? 'No employees found' : 'No employees available'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredEmployees.map((emp) => (
                                        <ListItem
                                            key={emp._id}
                                            disablePadding
                                            sx={{
                                                bgcolor: emp._id === employeeData?._id ? 'action.selected' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <ListItemButton
                                                onClick={() => handleSelectEmployee(emp._id)}
                                                selected={emp._id === employeeData?._id}
                                                sx={{ py: 1 }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={emp.photo}
                                                        sx={{
                                                            bgcolor: 'primary.main',
                                                            width: 40,
                                                            height: 40
                                                        }}
                                                    >
                                                        <PersonIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight="medium"
                                                            sx={{
                                                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {emp.name}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                            <Chip
                                                                label={emp.employee_type}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' }
                                                                }}
                                                            />
                                                            {emp.base_salary > 0 && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    ₹{emp.base_salary}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                )}
                            </List>
                        </Popover>
                    </Paper>

                    {/* Month Selection Section */}
                    <Paper elevation={0} sx={{
                        p: { xs: 1, sm: 1.5 },
                        mb: 3,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon color="primary" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="medium"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    {monthYear}
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                {/* Month Picker */}
                                <TextField
                                    size="small"
                                    type="month"
                                    value={format(selectedMonth, 'yyyy-MM')}
                                    onChange={handleMonthChange}
                                    sx={{
                                        width: { xs: '100%', sm: 185 },
                                        '& .MuiInputBase-input': {
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }
                                    }}
                                    disabled={loading}
                                />

                                {/* Navigation Buttons */}
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={handlePreviousMonth}
                                        disabled={loading}
                                    >
                                        <ArrowLeftIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleNextMonth}
                                        disabled={loading}
                                    >
                                        <ArrowRightIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>


                    <PrintableContainer id='monthly-attendance'>
                        {/* Attendance Summary Cards */}
                        <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 1 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    mb: 2,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                                className='section-heading '
                            >
                                Attendance Summary for {monthYear}
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: { xs: 1, sm: 2 },
                                justifyContent: { xs: 'center', sm: 'space-between' }
                            }} className="d-flex summary-section">
                                {summaryCards.map((card) => (
                                    <Box
                                        key={card.label}
                                        sx={{
                                            p: { xs: 1, sm: 1.5 },
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                            display: 'flex',
                                            flexDirection: { xs: 'row', sm: 'column' },
                                            alignItems: { xs: 'center', sm: 'start' },
                                            justifyContent: 'center',
                                            minWidth: { xs: 'calc(50% - 8px)', sm: 120 },
                                            flex: {
                                                xs: '1 0 calc(50% - 8px)',
                                                sm: '1 0 calc(33.33% - 16px)',
                                                md: '1 0 calc(16.66% - 16px)'
                                            },
                                            maxWidth: {
                                                xs: 'calc(50% - 8px)',
                                                sm: 'calc(33.33% - 16px)',
                                                md: 'calc(16.66% - 16px)'
                                            },
                                            minHeight: { xs: 70, sm: 80 },
                                            boxSizing: 'border-box',
                                            gap: { xs: 1, sm: 0 }
                                        }}
                                    >
                                        <Typography
                                            variant="h2"
                                            fontWeight="normal"
                                            color={`${card.color}.main`}
                                            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                                        >
                                            {card.value}
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mt: { xs: 0, sm: 0.5 }
                                        }}>
                                            <Chip
                                                label={card.short}
                                                size="small"
                                                color={card.color as any}
                                                sx={{
                                                    height: { xs: 18, sm: 20 },
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    minWidth: { xs: 25, sm: 30 }
                                                }}
                                                className='no-print'
                                            />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                            >
                                                {card.label}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>

                        {/* Detailed Salary Calculation Section */}
                        {employeeData?.base_salary && employeeData.base_salary > 0 && (
                            <Paper elevation={1} sx={{
                                p: { xs: 1.5, sm: 2 },
                                mb: 3,
                                borderRadius: 1,
                                bgcolor: 'grey.50'
                            }} >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                    className='section-heading'
                                >
                                    Detailed Salary Calculation for {monthYear}
                                </Typography>

                                {/* Salary Breakdown */}
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: "wrap",
                                    gap: { xs: 1.5, sm: 2 },
                                    mb: 2
                                }} className='d-flex'>
                                    <Box sx={{
                                        mb: 1,
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Base Salary
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}
                                        >
                                            ₹{salaryDetails.baseSalary}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        mb: 1,
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Daily Rate (based on 30 days)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            ₹{salaryDetails.dailyRate.toFixed(2)}/day
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        mb: 1,
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Hourly Rate
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            ₹{salaryDetails.hourlyRate.toFixed(2)}/hour
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        mb: 1,
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Overtime Rate (1.5x)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            ₹{salaryDetails.overtimeRate.toFixed(2)}/hour
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        mb: 1,
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Working Days in Month
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            {salaryDetails.workingDays} days
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Earnings Breakdown */}
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    color="success.main"
                                    sx={{
                                        mb: 1,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                    className='section-sub-heading'
                                >
                                    EARNINGS (+)
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: { xs: 1.5, sm: 2 },
                                    mb: 2
                                }} className='d-flex'>
                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Present Days ({salaryDetails.presentDays} days)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            ₹{salaryDetails.presentSalary.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Half Days ({salaryDetails.halfDays} days × 50%)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            ₹{salaryDetails.halfDaySalary.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Paid Leaves ({salaryDetails.paidLeaves} days)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            ₹{salaryDetails.paidLeaveSalary.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Overtime ({salaryDetails.overtimeHours} hours)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            ₹{salaryDetails.overtimeSalary.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Deductions Breakdown */}
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    color="error.main"
                                    sx={{
                                        mb: 1,
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                    className='section-sub-heading'
                                >
                                    DEDUCTIONS (-)
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: { xs: 1.5, sm: 2 },
                                    mb: 2
                                }} className='d-flex'>
                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Absent Days ({salaryDetails.absentDays} days)
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="error.main"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            - ₹{salaryDetails.deductions.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        flex: '1 0 calc(50% - 8px)',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 200 }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Advance Taken
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="error.main"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            - ₹{salaryDetails.totalAdvance.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Week Offs and Holidays (Informational) */}
                                <Box sx={{
                                    mb: 2,
                                    p: 1,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1
                                }} >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                            mb: 0.5,
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                        }}
                                    >
                                        Paid Days (Week Off & Holiday)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Week Offs: {salaryDetails.weekOffs} days
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            Holidays: {salaryDetails.holidays} days
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Net Salary Summary */}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    p: 1.5,
                                    bgcolor: 'success.light',
                                    borderRadius: 1,
                                    gap: { xs: 1.5, sm: 0 }
                                }} className='d-flex'>
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                            color="text.secondary"
                                            className='section-sub-heading'
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            Total Earnings
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            color="success.dark"
                                            sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}
                                        >
                                            ₹ {salaryDetails.totalEarnings.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: { xs: 'left', sm: 'left' } }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            className='section-sub-heading'
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            Net Salary Payable
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                            color="success.main"
                                            sx={{ fontSize: { xs: '1.25rem', sm: '2rem' } }}
                                        >
                                            ₹ {salaryDetails.netSalary.toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {/* Daily Attendance Table */}
                        {attendanceData.length > 0 ? (
                            <>
                                <Box sx={{
                                    overflowX: 'auto',
                                    '&::-webkit-scrollbar': {
                                        height: 6,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'grey.100',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'grey.400',
                                        borderRadius: 3,
                                    },
                                }}>
                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            minWidth: isMobile ? 800 : 'auto'
                                        }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '18%', sm: '15%' },
                                                        minWidth: 80
                                                    }}>Date</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '15%', sm: '15%' },
                                                        minWidth: 70
                                                    }}>Day</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '15%', sm: '12%' },
                                                        minWidth: 70
                                                    }}>Status</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '15%', sm: '12%' },
                                                        minWidth: 70
                                                    }}>In Time</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '15%', sm: '12%' },
                                                        minWidth: 70
                                                    }}>Out Time</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '14%', sm: '10%' },
                                                        minWidth: 60
                                                    }}>Working Hrs</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '14%', sm: '10%' },
                                                        minWidth: 60
                                                    }}>Overtime</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '14%', sm: '10%' },
                                                        minWidth: 60
                                                    }}>Advance</TableCell>
                                                    <TableCell sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                        py: 1,
                                                        width: { xs: '20%', sm: '10%' },
                                                        minWidth: 80
                                                    }}>Note</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {attendanceData.map((day, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="medium"
                                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                            >
                                                                {format(new Date(day.date), 'dd MMM yyyy')}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                            >
                                                                {format(new Date(day.date), 'EEE')}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <Chip
                                                                label={getStatusShort(day.attendance_status)}
                                                                color={getStatusColor(day.attendance_status) as any}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                                                    height: { xs: 20, sm: 22 },
                                                                    minWidth: { xs: 30, sm: 36 },
                                                                    fontWeight: 'medium'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            {day.in_time ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    fontFamily="monospace"
                                                                    fontWeight="medium"
                                                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                                >
                                                                    {formatTo12Hour(day.in_time)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            {day.out_time ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    fontFamily="monospace"
                                                                    fontWeight="medium"
                                                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                                >
                                                                    {formatTo12Hour(day.out_time)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="medium"
                                                                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                            >
                                                                {day.working_hour || 0}h
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            {day.overtime_hour > 0 ? (
                                                                <Chip
                                                                    label={`${day.overtime_hour}h`}
                                                                    color="warning"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                                                        height: { xs: 20, sm: 22 },
                                                                        fontWeight: 'medium'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            {day.advance_amount > 0 ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="error.main"
                                                                    fontWeight="medium"
                                                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                                                                >
                                                                    ₹{day.advance_amount}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            {day.note ? (
                                                                <Tooltip title={day.note} arrow>
                                                                    <Typography
                                                                        variant="caption"
                                                                        noWrap
                                                                        sx={{
                                                                            maxWidth: { xs: 80, sm: 100 },
                                                                            display: 'block',
                                                                            fontStyle: 'italic',
                                                                            fontSize: { xs: '0.6rem', sm: '0.75rem' }
                                                                        }}
                                                                    >
                                                                        {day.note}
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    fontStyle="italic"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                                {/* Table Summary Row */}
                                <Box sx={{
                                    mt: 2,
                                    p: { xs: 1.5, sm: 2 },
                                    bgcolor: 'grey.50',
                                    borderRadius: 1
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: { xs: 1, sm: 0 }
                                    }} className='d-flex'>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        >
                                            Total Days: <strong>{attendanceData.length}</strong>
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            gap: { xs: 1.5, sm: 3 },
                                            flexWrap: 'wrap'
                                        }} className='d-flex'>
                                            <Typography
                                                variant="body2"
                                                fontWeight="light"
                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                            >
                                                Working Hours: <strong>{summaryData.totalWorkingHours}h</strong>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="light"
                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                            >
                                                Overtime: <strong>{summaryData.totalOvertimeHours}h</strong>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="light"
                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                            >
                                                Advance: <strong>₹{summaryData.totalAdvance}</strong>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Paper sx={{
                                p: { xs: 3, sm: 4 },
                                textAlign: 'center',
                                bgcolor: 'grey.50'
                            }}>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    No attendance records found for {monthYear}
                                </Typography>
                            </Paper>
                        )}

                    </PrintableContainer>
                </CardContent>
            </Card>
        </>
    );
};

export default AttendanceListPerMonthOneEmployee;

